// Blocked Times Service — the ONE source of truth for barber time-blocks (lunch you took late,
// personal errands, "don't book me 2–3"). Was localStorage-only (per-device, invisible to other
// stations + all online apps + the shared availability brain). Now in Firestore like appointments,
// so a block set on any device instantly applies EVERYWHERE and blocks online booking. (Jose 2026-08-12)
//
// DATE/TIME ARE STRINGS ON PURPOSE — date = 'yyyy-MM-dd', startTime = 'HH:mm' — same shape as
// appointments, so the shared availability brain reads a block exactly like a taken slot and we never
// hit the `new Date("yyyy-MM-dd")` UTC-shift bug.

import { db } from '../config/firebase';
import {
  collection, onSnapshot, query, addDoc, updateDoc, deleteDoc, doc, getDocs, where,
} from 'firebase/firestore';

export interface BlockedTime {
  id: string;
  barberId: string;
  date: string;       // 'yyyy-MM-dd' (local)
  startTime: string;  // 'HH:mm' (24h, local)
  duration: number;   // minutes
  reason?: string;
  notes?: string;
  shopId?: string;
  createdAt?: Date;
  // RECURRING BLOCKS (Jose 2026-11-03): a repeating block (e.g. every Thursday 11–12) shares one
  // recurrenceGroupId across all its occurrences, mirroring recurring appointments.
  recurrenceGroupId?: string;
  recurrenceIndex?: number;
  forever?: boolean;
}

const COLLECTION = 'blockedTimes';
const MIGRATION_KEY = 'slikpos_blocked_times_migrated_v1';
const LEGACY_KEY = 'slikpos_blocked_times';

type Listener = (blocks: BlockedTime[]) => void;

class BlockedTimesService {
  private blocks: BlockedTime[] = [];
  private listeners: Set<Listener> = new Set();
  private started = false;

  /** Begin the real-time Firestore subscription (idempotent). */
  start(): void {
    if (this.started) return;
    this.started = true;
    try {
      const ref = collection(db, COLLECTION);
      onSnapshot(query(ref), (snap) => {
        this.blocks = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            barberId: data.barberId,
            date: data.date,
            startTime: data.startTime,
            duration: data.duration || 30,
            reason: data.reason,
            notes: data.notes,
            shopId: data.shopId,
            createdAt: data.createdAt?.toDate?.() || undefined,
            recurrenceGroupId: data.recurrenceGroupId,
            recurrenceIndex: data.recurrenceIndex,
            forever: data.forever,
          } as BlockedTime;
        });
        this.notify();
      }, (err) => console.error('[blockedTimes] snapshot error:', err));
    } catch (e) {
      console.error('[blockedTimes] start failed:', e);
    }
    // One-time migration of any legacy localStorage blocks → Firestore.
    this.migrateFromLocalStorage();
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    cb(this.blocks);
    return () => { this.listeners.delete(cb); };
  }

  private notify(): void {
    this.listeners.forEach((cb) => { try { cb(this.blocks); } catch { /* listener error */ } });
  }

  getAll(): BlockedTime[] { return this.blocks; }

  /** This barber's blocks on a given yyyy-MM-dd. */
  getForBarberDate(barberId: string, date: string): BlockedTime[] {
    return this.blocks.filter((b) => b.barberId === barberId && b.date === date);
  }

  async add(block: Omit<BlockedTime, 'id'>): Promise<BlockedTime> {
    const ref = collection(db, COLLECTION);
    const docRef = await addDoc(ref, { ...block, createdAt: new Date() } as any);
    const created = { ...block, id: docRef.id } as BlockedTime;
    if (!this.blocks.some((b) => b.id === created.id)) this.blocks = [...this.blocks, created];
    this.notify();
    return created;
  }

  /** Write a repeating SERIES of blocks that all share one recurrenceGroupId (Jose 2026-11-03). */
  async addSeries(blocks: Array<Omit<BlockedTime, 'id'>>): Promise<void> {
    const ref = collection(db, COLLECTION);
    for (const b of blocks) {
      const docRef = await addDoc(ref, { ...b, createdAt: new Date() } as any);
      const created = { ...b, id: docRef.id } as BlockedTime;
      if (!this.blocks.some((x) => x.id === created.id)) this.blocks = [...this.blocks, created];
    }
    this.notify();
  }

  /** Remove every block in a recurring series (optionally only from a given date forward). Optimistic + revert. */
  async removeSeries(recurrenceGroupId: string, fromDate?: string): Promise<void> {
    const victims = this.blocks.filter((b) => b.recurrenceGroupId === recurrenceGroupId && (!fromDate || b.date >= fromDate));
    if (!victims.length) return;
    const prev = this.blocks;
    this.blocks = this.blocks.filter((b) => !victims.some((v) => v.id === b.id));
    this.notify();
    try {
      for (const v of victims) await deleteDoc(doc(db, COLLECTION, v.id));
    } catch (e) {
      console.error('[blockedTimes] removeSeries failed — reverting:', e);
      this.blocks = prev; this.notify(); throw e;
    }
  }

  async update(id: string, updates: Partial<BlockedTime>): Promise<void> {
    // H8/H9 FIX (Jose 2026-10-29 audit): optimistic update stays for snappy UI, but if the Firestore
    // write fails we REVERT the in-memory state (so the grid reflects the truth, not a phantom save) and
    // RETHROW so the caller can show a real error instead of a lying "Block updated" toast.
    const prev = this.blocks;
    this.blocks = this.blocks.map((b) => (b.id === id ? { ...b, ...updates } : b));
    this.notify();
    try {
      await updateDoc(doc(db, COLLECTION, id), updates as any);
    } catch (e) {
      console.error('[blockedTimes] update failed — reverting:', e);
      this.blocks = prev;
      this.notify();
      throw e;
    }
  }

  async remove(id: string): Promise<void> {
    // H8/H9 FIX: same contract as update — revert + rethrow on a failed delete so the caller never
    // reports a removal that didn't persist (it would otherwise reappear on the next device/refresh).
    const prev = this.blocks;
    this.blocks = this.blocks.filter((b) => b.id !== id);
    this.notify();
    try {
      await deleteDoc(doc(db, COLLECTION, id));
    } catch (e) {
      console.error('[blockedTimes] remove failed — reverting:', e);
      this.blocks = prev;
      this.notify();
      throw e;
    }
  }

  /** Move any pre-Firestore localStorage blocks into Firestore once, then stop reading localStorage. */
  private async migrateFromLocalStorage(): Promise<void> {
    try {
      if (localStorage.getItem(MIGRATION_KEY)) return;
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) { localStorage.setItem(MIGRATION_KEY, '1'); return; }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) { localStorage.setItem(MIGRATION_KEY, '1'); return; }
      const pad = (n: number) => String(n).padStart(2, '0');
      const toYmd = (v: any) => { const d = new Date(v); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
      const toHm = (v: any) => { const d = new Date(v); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
      for (const bt of parsed) {
        // Legacy blocks stored Date objects (ISO strings after JSON) for date + startTime.
        await this.add({
          barberId: bt.barberId,
          date: typeof bt.date === 'string' && bt.date.length === 10 && bt.date.includes('-') ? bt.date : toYmd(bt.date),
          startTime: typeof bt.startTime === 'string' && bt.startTime.length <= 5 && bt.startTime.includes(':') ? bt.startTime : toHm(bt.startTime),
          duration: bt.duration || 30,
          reason: bt.reason,
          notes: bt.notes,
        });
      }
      localStorage.setItem(MIGRATION_KEY, '1');
      localStorage.removeItem(LEGACY_KEY); // no more per-device source
      console.log(`[blockedTimes] migrated ${parsed.length} legacy block(s) to Firestore`);
    } catch (e) {
      console.error('[blockedTimes] migration failed (non-fatal):', e);
    }
  }
}

export const blockedTimesService = new BlockedTimesService();

/**
 * Map blocks to the shared availability brain's occupancy shape (AvailAppointment-like). A block is
 * treated exactly like a booked appointment for that barber → bookableSlots() excludes it. Feed these
 * alongside real appointments into bookableSlots(...) / hasApptConflict(...) on EVERY surface.
 */
export function blocksAsOccupancy(blocks: BlockedTime[]): Array<{ barberId: string; time: string; duration: number; status: string }> {
  return blocks.map((b) => ({ barberId: b.barberId, time: b.startTime, duration: b.duration || 30, status: 'confirmed' }));
}
